import React from 'react';
import PropTypes from 'prop-types';
import ReactDOM from 'react-dom';
import $ from 'jquery';
import { Popover } from 'antd';
import _ from 'lodash';
import { css } from 'emotion';
import RollupError from 'components/core/RollupError';
import ModalsService from 'services/ModalsService';
import TabsService from 'services/TabsService';
import ProcessService from 'services/ProcessService';
import ComponentUtils from 'utils/ComponentUtils';
import { getErrorPopoverStyle, popoverErrorStyle } from 'styles/base.js';
import Constants from '../../../utils/Constants';

/**
 * Modified from https://github.com/kaivi/riek, v1.1.0
 *
 * RIE components need to implement the following methods:
 *
 * 1)
 */
export default class RIEBase extends React.Component {
    static propTypes = {
        value: PropTypes.any,
        change: PropTypes.func,
        fieldPath: PropTypes.array,
        editProps: PropTypes.object,
        defaultProps: PropTypes.object,
        isDisabled: PropTypes.bool,
        className: PropTypes.string,
        beforeStart: PropTypes.func,
        afterStart: PropTypes.func,
        beforeFinish: PropTypes.func,
        afterFinish: PropTypes.func,
        isFullWidth: PropTypes.bool,
        tooltipPosition: PropTypes.string,
        urlCommit: PropTypes.bool, // field is used in the url and will break UI if null
        checkUnique: PropTypes.object, // field is used in url and will break UI if not unique; include 'label' for error message, 'list' array to check against
        processId: PropTypes.string,
        processErrors: PropTypes.object,
        onChangeData: PropTypes.object,
        horizontalAlignment: PropTypes.string, // 'left' or 'right'
        verticalAlignment: PropTypes.string, // 'top' or 'bottom'
        minWidth: PropTypes.string,
        formDisplay: PropTypes.bool,
        style: PropTypes.string,
        forceChange: PropTypes.bool, // force change function to run; used for if there is secondary input within the rie component
        disabledMessage: PropTypes.string
    };

    static defaultProps = {
        horizontalAlignment: 'left',
        verticalAlignment: 'top'
    };

    constructor(props) {
        super(props);

        if (typeof this.props.value === 'undefined') {
            throw new Error("Input missing 'value' prop");
        }

        this.state = {
            editing: false,
            newValue: null,
            isTooltipActive: false
        };

        // create a unique id for this component so the tooltip can find it in the dom
        this.id = this.getUuid();
    }

    componentDidMount() {
        // We are keeping track of whether this component has been mounted or not since isMounted() is officially
        // deprecated
        this._ismounted = true;
    }

    componentWillUnmount() {
        this._ismounted = false;
    }

    /**
     * Called when props are passed to the component, NOT when a state change occurs after calling setState().
     * We can safely call setState() here and it won't re-render.
     * @param nextProps
     */
    componentWillReceiveProps = (nextProps) => {
        if (nextProps['value'] !== this.props['value']) {
            // React is re-rendering us with a different value, so we should stop editing and start over
            this.setState({ editing: false, newValue: null });
        }
    };

    getUuid() {
        const s4 = () => {
            return Math.floor((1 + Math.random()) * 0x10000)
                .toString(16)
                .substring(1);
        };
        const uuid = s4() + s4() + s4() + s4() + s4() + s4() + s4() + s4();

        // html5 id must start with a letter
        return `id-${uuid}`;
    }

    /**
     * Called after the component is re-rendered.  It is called after every state change.
     * @param prevProps
     * @param prevState
     */
    componentDidUpdate(prevProps, prevState) {

        // If we have just transitioned into the edit state, then we need to make sure the editable component has focus
        if (this.state.editing && !prevState.editing) {
            this.highlightComponentText();

            // Make the editable component is visible in the window.

            // After we upgraded react, the DOM scrollIntoView method would not work correctly, so I've switched to
            // using jquery to animate changing the scroll position on the scrollable container.  In order for
            // this to work on all pages, the scrollable component needs to have the "rie-scrollable" class defined.
            // I am assuming that only one element on the page will have rie-scrollable at a time.  If this changes,
            // we will need to modify this function.

            const editableContainer = ReactDOM.findDOMNode(this);
            let scrollable = $('.rie-scrollable');

            // if there are more than one scrollable, get the last one
            // this could happen if we are in a modal dialog on top of a page
            scrollable = $(scrollable[scrollable.length - 1]);

            if (scrollable.offset()) {
                const config = {};

                // First check if we need to scroll vertically (i.e., if the element is outside the scrollable's viewport)
                const viewportTop = scrollable.scrollTop();
                const viewportBottom = viewportTop + scrollable.height();
                const elemTop = editableContainer.offsetTop;
                // Need to use the height of the editable-wrapper child because the editable-component div is always
                // computing 0 height.  Not sure why...
                const elemBottom = elemTop + editableContainer.firstElementChild.getBoundingClientRect().height;

                if (elemBottom > viewportBottom) {
                    config.scrollTop = viewportTop + (elemBottom - viewportBottom);
                } else if (elemTop < viewportTop) {
                    config.scrollTop = viewportTop + (elemTop - viewportTop);
                }

                // Now check if we need to scroll horizontally (i.e., if the element is outside the scrollable's viewport)
                const viewportLeft = scrollable.offset().left;
                const viewportRight = viewportLeft + scrollable.width();
                const elemLeft = editableContainer.offsetLeft - scrollable.scrollLeft();
                const elemRight = elemLeft + editableContainer.firstElementChild.getBoundingClientRect().width;

                if (elemRight > viewportRight) {
                    config.scrollLeft = scrollable.scrollLeft() + (elemRight - viewportRight);
                } else if (elemLeft < viewportLeft) {
                    config.scrollLeft = scrollable.scrollLeft() + (elemLeft - viewportLeft);
                }

                if (!_.isEmpty(config)) {
                    scrollable.animate(config, 1000, 'linear');
                }
            }
        }
    };

    /**
     * By default, each component MUST declare one input type element with the "input" ref that will take focus
     * when the component goes into edit mode.
     */
    highlightComponentText = () => {
        const inputElem = ReactDOM.findDOMNode(this.refs.input);
        if (inputElem) {
            inputElem.focus();
            if (inputElem.setSelectionRange && inputElem.type !== 'radio') {
                inputElem.setSelectionRange(0, inputElem.value.length);
            }
        }
    };

    makeClassString = () => {
        const classNames = [];
        classNames.push('editable-component');
        if (this.props.className) {
            classNames.push(this.props.className);
        }
        if (this.state.editing) {
            classNames.push('editing');
        }
        if (this.props.isDisabled) {
            classNames.push('disabled');
        }
        if (this.props.formDisplay) {
            classNames.push('component');
        }

        return `${classNames.join(' ')}`;
    };

    startEditing = (e) => {

        // do nothing when right mouse button pressed
        if (e.nativeEvent.which === 3) return;

        // do nothing if component is disabled
        if (this.props.isDisabled) return;

        // There is a timing issue when directly clicking from one RIE component in edit mode to another.
        // Sometimes the componentWillReceiveProps is resetting editing to false before it gets rendered, and so it
        // never goes into edit mode.
        setTimeout(() => {
            if (this.props.beforeStart) this.props.beforeStart();
            this.setState({ editing: true });
            if (this.props.afterStart) this.props.afterStart();

        }, 100);
    };

    canCommit(newValue) {
        let results = {
            canCommit: true,
            message: ''
        };
        const state = ProcessService.getMyState(ProcessService.getStore().getState());

        if (this.props.urlCommit) {
            if (newValue === '' || newValue === null) {
                results.canCommit = false;
                results.message = 'Name cannot be blank.';
            } else {
                // We also have to check for URL reserved characters which would cause the routing to crash
                // eslint-disable-next-line
                const urlReservedCharPattern = /[\/?=&:#%]+/;
                if (urlReservedCharPattern.test(newValue)) {
                    results.canCommit = false;
                    results.message = 'Names cannot contain URL reserved characters (# / ? : = & %).';
                }
            }

        }

        if (this.props.checkUnique) {
            const checkValue = newValue.value ? newValue.value : newValue;
            if (this.props.checkUnique.list.indexOf(checkValue) !== -1) {
                results.canCommit = false;
                results.message = `A ${this.props.checkUnique.label} already exists with that name.`;
            }
        }

        if (this.props.fieldPath === 'name') { // We are changing the process name, so we have to check it's unique
            results = this.canProcessNameCommit(state, results, newValue);
        }

        return results;
    }

    canProcessNameCommit(state, results, newName) {
        const newResults = results;
        const oldName = this.props.value;

        if (newName !== oldName) {
            let originalName = '';
            if (state.originalCopiesById[oldName]) {
                originalName = state.originalCopiesById[oldName].name;
            }

            // If we renamed back to the original name, then it's ok.  Otherwise, we have to
            // make sure the new name isn't already being used by another processs.
            if (newName !== originalName) {
                const processNames = state.sortedIds;
                const workingCopyNames = Object.keys(state.workingCopiesById);
                if (workingCopyNames.indexOf(newName) !== -1 || processNames.indexOf(newName) !== -1) {
                    newResults.canCommit = false;
                    newResults.message = 'A process already exists with that name.';
                }
            }
        }

        return newResults;
    }

    finishEditing = () => {
        if (this.props.beforeFinish) this.props.beforeFinish();
        const newValue = this.getNewValue();

        if (this.props.value !== newValue || this.props.forceChange) {
            const canCommitResults = this.canCommit(newValue);
            if (canCommitResults.canCommit) {
                this.props.change(this.props.fieldPath, newValue, this.props.onChangeData); // push value through to the store
            } else {
                // pop up error message saying the edit was reverted
                ModalsService.openModal({
                    id: 'rie_error',
                    modalType: ModalsService.TYPE_ERROR,
                    modalTitle: 'Edit Failed',
                    modalMessage: `${canCommitResults.message}  Edit will be reverted.`
                });
            }
        }
        this.cancelEditing();

        if (this.props.afterFinish) this.props.afterFinish();
    };

    cancelEditing = () => {
        this.setState({ editing: false, newValue: null });
    };

    /**
     * Components should be storing the new value in their state as the component is modified.
     */
    getNewValue = () => {
        // return the latest state set by the component widgets, or
        // the latest the original props.value if nothing has changed
        if (this.state.newValue !== null && this.state.newValue !== undefined) {
            return this.state.newValue;
        } else {
            return this.props.value;
        }
    };

    keyDown = (event) => {
        if (event.keyCode === 13) { // Enter
            // stop OK on confirm modal from being fired before finish editing runs
            event.stopPropagation();
            this.finishEditing();

        } else if (event.keyCode === 27) { // Escape
            this.cancelEditing();
        }
    };

    elementBlur = (e) => {
        const componentArea = ReactDOM.findDOMNode(this.refs.container);

        //explicitOriginalTarget for firefox/safari on mac
        const clickedElement = e.relatedTarget || document.activeElement;
        if (!componentArea.contains(clickedElement)) {
            this.finishEditing();
        }
    };

    /**
     * This only works on string fields
     * @param event
     */
    textChanged = (event) => {
        const newValue = event.target.value.trim();
        this.setState({ newValue });
    };

    getIsTooltipActive(id) {
        if (id !== this.id) {
            return this.state[id];
        } else {
            return this.state.isTooltipActive;
        }
    }

    /**
     * Concatenate the list of error messages into a single error
     * @returns string
     */
    getErrorMessage() {
        let message = '';
        this.errorList.forEach((error, index) => {
            if (index > 0) {
                message += '.....';
            }
            message += error.message;
        });
        return message;
    }

    getPopoverStyle() {
        return getErrorPopoverStyle(this.errorList);
    }

    showTooltip = (id) => {
        if (id) {
            return () => {
                this.setState({ [id]: true });
            };
        }
        return () => {
            this.setState({ isTooltipActive: true });
        };
        
    };

    hideTooltip = (id) => {
        if (id) {
            return () => {
                this.setState({ [id]: false });
            };
        }
        return () => {
            return this.setState({ isTooltipActive: false });
        };
    };

    getMinWidth = () => {
        return '100%';
    };

    getEditableWrapperStyle = () => {
        let minWidth = this.getMinWidth();
        if (this.props.minWidth) {
            minWidth = this.props.minWidth; // so views can override for specific cases
        }
        return { minWidth };
    };

    doNothing = () => {

    };

    getStartEditingProps = () => {
        return { onClick: this.startEditing, onFocus: this.startEditing };
    };

    renderReadComponent() {
        const value = this.getNewValue();

        return (
            <span
                tabIndex="0"
                className={this.makeClassString()}
                {...this.getStartEditingProps()}
                {...this.props.defaultProps}
            >
                {value}
            </span>
        );
    }

    renderNormalComponent() {
        let size = 50;
        let value = this.props.value;
        if (value && value.length > 50) {
            size = this.props.value.length + 10;
        }

        if (value === null) {
            value = '';
        }

        return (
            <input
                className={this.makeClassString()}
                {...this.getStartEditingProps()}
                value={value}
                onChange={this.textChanged}
                size={size}
                ref="input"
                {...this.props.editProps}
            />
        );
    }

    renderEditingComponent = () => {
        let size = 50;
        if (this.props.value && this.props.value.length > 50) {
            size = this.props.value.length + 10;
        }

        return (
            <input
                className={this.makeClassString()}
                defaultValue={this.props.value}
                onChange={this.textChanged}
                size={size}
                ref="input"
                {...this.props.editProps}
            />
        );
    };

    render = () => {
        let fullWidthClass = 'full-width';
        if (this.props.isFullWidth === false) {
            fullWidthClass = '';
        }
        const horzAlignment = `align-${this.props.horizontalAlignment}`;
        const vertAlignment = `align-${this.props.verticalAlignment}`;

        let componentToRender = null;

        if (this.state.editing) {
            componentToRender = (
                <div
                    className={`editable-container ${fullWidthClass}`}
                    onBlur={this.elementBlur}
                    onKeyDown={this.keyDown}
                    ref="container"
                    style={this.props.style}
                    tabIndex={0}
                >
                    <div className={`editable-wrapper ${horzAlignment} ${vertAlignment}`} style={this.getEditableWrapperStyle()}>
                        <RollupError
                            fieldPath={this.props.fieldPath}
                            processId={this.props.processId}
                            processErrors={this.props.processErrors}
                        />
                        {this.renderEditingComponent()}
                        <div className="save-container">
                            <div className="save-options" tabIndex="1">
                                <button
                                    type="button"
                                    onClick={this.finishEditing}
                                    title="Finish Editing"
                                >
                                    <i className="fa fa-check" aria-hidden="true" />
                                </button>
                                <button
                                    type="button"
                                    onClick={this.cancelEditing}
                                    title="Cancel Editing"
                                >
                                    <i className="fa fa-times" aria-hidden="true" />
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            );
        } else {
            let errorMessage = '';
            let popoverStyle = null;
            const tabState = TabsService.getMyState(TabsService.getStore().getState());
            const processState = ProcessService.getMyState(ProcessService.getStore().getState());
            const activeTab = tabState.activeTab;

            if (this.props.isDisabled) {
                errorMessage = Constants.PERMISSIONS_ERROR_MESSAGE;
                popoverStyle = popoverErrorStyle;

                if (this.props.disabledMessage) {
                    errorMessage = this.props.disabledMessage;
                }

                if (TabsService.getObjFromTabText(activeTab).revisionNum) {
                    errorMessage = 'Old revisions are not editable. To edit, save this revision which will make it the current version.';
                }

                if (processState.processesById && processState.processesById[this.props.processId] && processState.processesById[this.props.processId].deprecated) {
                    errorMessage = Constants.DEPRECATED_DISABLED_MESSAGE;
                }

            }

            componentToRender = (
                <a
                    id={`${this.id}`}
                    className={`rie-tooltip ${fullWidthClass} ${css`display:flex`}`}
                    style={this.props.style}
                >
                    <RollupError
                        fieldPath={this.props.fieldPath}
                        processId={this.props.processId}
                        processErrors={this.props.processErrors}
                    />
                    { this.props.formDisplay ? this.renderNormalComponent() : this.renderReadComponent() }
                </a>
            );

            if (errorMessage) {
                const tooltipPosition = this.props.tooltipPosition ? this.props.tooltipPosition : 'topLeft';
                componentToRender = (
                    <Popover content={errorMessage}
                        placement={tooltipPosition}
                        overlayClassName={popoverStyle}
                    >
                        {componentToRender}
                    </Popover>
                );

            } 
        }

        return componentToRender;
    };
}



// WEBPACK FOOTER //
// ./src/components/core/editable/RIEBase.js